import { type JSX } from "react";
import { cn } from "./lib/utils";
import type { NavGroupItem } from "./nav-model";
import { NavIcon } from "./NavIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

export interface NavItemProps {
  item: NavGroupItem;
  active: boolean;
  collapsed: boolean;
  onNavigate: (route: string) => void;
}

/**
 * A single sidebar nav entry. Rendered as an anchor, not a button, so the href
 * contract holds and middle-click / open-in-new-tab keep working; the click
 * handler intercepts plain left clicks for client-side routing.
 */
export function NavItem({ item, active, collapsed, onNavigate }: NavItemProps): JSX.Element {
  const link = (
    <a
      href={item.href}
      data-platform-section-link=""
      data-nav-id={item.id}
      aria-current={active ? "page" : undefined}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
          return;
        }

        event.preventDefault();
        onNavigate(item.route);
      }}
      className={cn(
        "flex h-8 items-center gap-2 rounded-pill text-base no-underline",
        "transition-[background-color,color,transform] duration-150 ease-out active:scale-[.99] motion-reduce:transition-none motion-reduce:active:scale-100",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "focus-visible:ring-offset-2 focus-visible:ring-offset-shell-canvas",
        collapsed ? "w-8 justify-center px-0" : "w-full px-3",
        active
          ? "bg-nav-active text-sidebar-text"
          : // Inactive routes keep full text contrast and use the sidebar hover
            // surface to distinguish the pointer state.
            "text-sidebar-text hover:bg-sidebar-surface-hover"
      )}
    >
      {item.icon ? <NavIcon icon={item.icon} className="size-4 shrink-0 object-contain" /> : null}
      {!collapsed && <span className="truncate">{item.label}</span>}
    </a>
  );

  if (!collapsed) {
    return link;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}
